import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { apiCall } from '../../utils/apiUtils';
import { showSuccessToast, showErrorToast, showInfoToast } from '../../utils/toastUtils';

const BulkImportExport = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState(user?.role === 'vendor' ? 'export' : 'import');
  const [columnGroups, setColumnGroups] = useState({});
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [exportFilters, setExportFilters] = useState({
    client_name: '',
    start_date: '',
    end_date: '',
    state: '',
    status: ''
  });
  const [importFiles, setImportFiles] = useState([]);
  const [importPreview, setImportPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [states, setStates] = useState([]);

  // Get the appropriate API endpoints based on user role
  const getApiEndpoints = () => {
    switch (user?.role) {
      case 'client':
        return {
          columnGroups: '/api/client/export-column-groups',
          importTemplate: '/api/client/import-template',
          importPreview: '/api/client/import-preview',
          importOrders: '/api/client/import-orders',
          exportOrders: '/api/client/export-orders',
          clients: '/api/admin/clients', // Not used for clients
          states: '/api/states/from-orders'
        };
      case 'vendor':
        return {
          columnGroups: '/api/vendor/export-column-groups',
          importTemplate: null, // Vendors can't import
          importPreview: null,
          importOrders: null,
          exportOrders: '/api/vendor/export-orders',
          clients: '/api/admin/clients', // Not used for vendors
          states: '/api/states/from-orders'
        };
      default: // admin
        return {
          columnGroups: '/api/admin/export-column-groups',
          importTemplate: '/api/admin/import-template',
          importPreview: '/api/admin/import-preview',
          importOrders: '/api/admin/import-orders',
          exportOrders: '/api/admin/export-orders',
          clients: '/api/admin/clients',
          states: '/api/admin/states'
        };
    }
  };

  const apiEndpoints = getApiEndpoints();

  useEffect(() => {
    fetchColumnGroups();
    if (user?.role === 'admin') {
      fetchClients();
      fetchStates();
    } else {
      fetchStates(); // For non-admin users, fetch states from their accessible orders
    }
  }, [user?.role]);

  const fetchColumnGroups = async () => {
    try {
      const response = await apiCall(apiEndpoints.columnGroups);
      setColumnGroups(response);
      const allColumns = Object.values(response).flatMap(group => Object.keys(group));
      setSelectedColumns(allColumns);
    } catch (error) {
      showErrorToast('Failed to load column groups');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await apiCall(apiEndpoints.clients);
      setClients(response);
    } catch (error) {
      showErrorToast('Failed to load clients');
    }
  };

  const fetchStates = async () => {
    try {
      const response = await apiCall(apiEndpoints.states);
      setStates(Array.isArray(response) ? response : (response.states || []));
    } catch (error) {
      showErrorToast('Failed to load states');
    }
  };

  const downloadTemplate = async () => {
    if (!apiEndpoints.importTemplate) {
      showErrorToast('Import functionality not available for your role');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${apiEndpoints.importTemplate}`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to download template');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'order_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showSuccessToast('Import template downloaded successfully');
    } catch (error) {
      showErrorToast('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setImportFiles(files);
    setImportPreview(null);
    setIsModalOpen(false);
  };

  const previewImport = async () => {
    if (!apiEndpoints.importPreview) {
      showErrorToast('Import functionality not available for your role');
      return;
    }

    if (importFiles.length === 0) {
      showErrorToast('Please select files to import');
      return;
    }

    try {
      const formData = new FormData();
      importFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${apiEndpoints.importPreview}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) throw new Error('Failed to preview import');
      
      const data = await response.json();
      console.log('Import Preview Response:', data);
      setImportPreview(data.previews);
      setIsModalOpen(true);
      showSuccessToast('Import preview generated successfully');
    } catch (error) {
      showErrorToast('Failed to preview import files');
    }
  };

  const removeFile = (indexToRemove) => {
    const newFiles = importFiles.filter((_, index) => index !== indexToRemove);
    const newPreview = importPreview.filter((_, index) => index !== indexToRemove);
    setImportFiles(newFiles);
    setImportPreview(newPreview.length > 0 ? newPreview : null);
    if (newFiles.length === 0) {
      setIsModalOpen(false);
      document.getElementById('import-files').value = '';
    }
  };

  const executeImport = async () => {
    if (!apiEndpoints.importOrders) {
      showErrorToast('Import functionality not available for your role');
      return;
    }

    if (!importPreview) {
      showErrorToast('Please preview files before importing');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      importFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${apiEndpoints.importOrders}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) throw new Error('Failed to import orders');
      
      const result = await response.json();
      
      if (result.success) {
        showSuccessToast(`Import completed! ${result.total_orders_created} orders created from ${result.processed_files} files`);
      } else {
        showInfoToast(`Import completed with issues. ${result.total_orders_created} orders created, ${result.failed_rows} rows failed`);
      }
      
      setImportFiles([]);
      setImportPreview(null);
      setIsModalOpen(false);
      document.getElementById('import-files').value = '';
    } catch (error) {
      showErrorToast('Failed to import orders');
    } finally {
      setIsUploading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const executeExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      
      if (selectedColumns.length > 0) {
        params.append('columns', selectedColumns.join(','));
      }
      
      Object.entries(exportFilters).forEach(([key, value]) => {
        // Skip client_name filter for non-admin users since they shouldn't see it
        if (key === 'client_name' && user?.role !== 'admin') return;
        if (value) params.append(key, value);
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${apiEndpoints.exportOrders}?${params}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to export orders');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Set role-specific filename
      const timestamp = new Date().toISOString().slice(0, 10);
      const rolePrefix = user?.role === 'client' ? 'client' : user?.role === 'vendor' ? 'vendor' : 'admin';
      a.download = `${rolePrefix}_orders_export_${timestamp}.xlsx`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showSuccessToast('Orders exported successfully');
    } catch (error) {
      showErrorToast('Failed to export orders');
    } finally {
      setIsExporting(false);
    }
  };

  const toggleColumnGroup = (groupName) => {
    const groupColumns = Object.keys(columnGroups[groupName] || {});
    const allSelected = groupColumns.every(col => selectedColumns.includes(col));
    
    if (allSelected) {
      setSelectedColumns(prev => prev.filter(col => !groupColumns.includes(col)));
    } else {
      setSelectedColumns(prev => [...new Set([...prev, ...groupColumns])]);
    }
  };

  const toggleColumn = (column) => {
    setSelectedColumns(prev => 
      prev.includes(column) 
        ? prev.filter(col => col !== column)
        : [...prev, column]
    );
  };

  // Get role-specific title and description
  const getRoleSpecificContent = () => {
    switch (user?.role) {
      case 'client':
        return {
          title: 'Bulk Import/Export Management (Client)',
          importDesc: 'Import orders for your account from CSV/Excel files',
          exportDesc: 'Export your orders with customized columns and filters'
        };
      case 'vendor':
        return {
          title: 'Export Management (Vendor)',
          importDesc: null,
          exportDesc: 'Export your assigned orders with customized columns and filters'
        };
      default:
        return {
          title: 'Bulk Import/Export Management (Admin)',
          importDesc: 'Import orders for all clients from CSV/Excel files',
          exportDesc: 'Export all orders with customized columns and filters'
        };
    }
  };

  const roleContent = getRoleSpecificContent();

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{roleContent.title}</h2>
        
        {/* Section Tabs - Hide import tab for vendors */}
        <div className="flex border-b border-gray-200 mb-6">
          {user?.role !== 'vendor' && (
            <button
              onClick={() => setActiveSection('import')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeSection === 'import'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📥 Bulk Import
            </button>
          )}
          <button
            onClick={() => setActiveSection('export')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeSection === 'export'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📤 Bulk Export
          </button>
        </div>

        {/* Import Section - Hidden for vendors */}
        {activeSection === 'import' && user?.role !== 'vendor' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Import Instructions</h3>
              <p className="text-sm text-blue-800 mb-2">{roleContent.importDesc}</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Download the Excel template to get the correct format</li>
                <li>• Supports both CSV and Excel files (.csv, .xlsx, .xls)</li>
                <li>• Multiple files can be uploaded at once</li>
                <li>• System will auto-detect new columns and extend schema</li>
                <li>• Order IDs must be unique (system will resolve conflicts)</li>
                {user?.role === 'client' && (
                  <li>• Orders will be imported for your account only</li>
                )}
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Step 1: Download Template</h3>
                <button
                  onClick={downloadTemplate}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Import Template
                </button>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Step 2: Upload Files</h3>
                <input
                  id="import-files"
                  type="file"
                  multiple
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
                {importFiles.length > 0 && (
                  <div className="text-sm text-gray-600">
                    {importFiles.length} file(s) selected: {importFiles.map(f => f.name).join(', ')}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={previewImport}
                disabled={importFiles.length === 0}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
              >
                Preview Import
              </button>
              <button
                onClick={executeImport}
                disabled={!importPreview || isUploading}
                className="flex-1 bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
              >
                {isUploading ? 'Importing...' : 'Execute Import'}
              </button>
            </div>
          </div>
        )}

        {/* Modal Overlay for Import Preview */}
        {isModalOpen && importPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl h-[80vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Import Preview</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {importPreview.map((preview, index) => (
                  <div key={index} className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold text-gray-900">{preview.filename}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2 text-sm">
                          <div>Total Rows: <span className="font-medium">{preview.total_rows}</span></div>
                          <div>Existing Columns: <span className="font-medium">{preview.existing_columns.length}</span></div>
                          <div>New Columns: <span className="font-medium">{preview.new_columns.length}</span></div>
                          <div>Warnings: <span className="font-medium text-yellow-600">{preview.warnings.length}</span></div>
                        </div>
                        {preview.warnings.length > 0 && (
                          <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                            <div className="text-sm text-yellow-800">
                              {preview.warnings.map((warning, i) => (
                                <div key={i}>⚠️ {warning}</div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="bg-red-600 text-white py-1 px-3 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                    {/* Table Preview */}
                    {preview.rows && preview.rows.length > 0 && (
                      <div className="bg-white rounded-lg p-4 border">
                        <h4 className="font-semibold text-gray-900 mb-2">Data Preview (First {preview.rows.length} Rows)</h4>
                        <div className="overflow-x-auto max-h-96">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                {Object.keys(preview.rows[0]).map((col, i) => (
                                  <th
                                    key={i}
                                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    {col}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {preview.rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                  {Object.values(row).map((value, colIndex) => (
                                    <td
                                      key={colIndex}
                                      className="px-4 py-2 whitespace-nowrap text-sm text-gray-900"
                                    >
                                      {value != null ? value.toString() : ''}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Modal Footer */}
              <div className="flex justify-end p-4 border-t">
                <button
                  onClick={closeModal}
                  className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Section */}
        {activeSection === 'export' && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Export Options</h3>
              <p className="text-sm text-green-800">
                Select columns and apply filters to customize your export. The export will include a summary sheet with metadata.
              </p>
            </div>

            {/* Filters */}
            <div className="bg-white border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Filters</h3>
                <button
                  type="button"
                  onClick={() => setExportFilters({
                    client_name: '',
                    start_date: '',
                    end_date: '',
                    state: '',
                    status: ''
                  })}
                  className="text-sm text-gray-600 hover:text-indigo-600 transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear All Filters
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Client Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Client
                  </label>
                  <select
                    value={exportFilters.client_name}
                    onChange={(e) => setExportFilters(prev => ({...prev, client_name: e.target.value}))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600"
                  >
                    <option value="">All Clients</option>
                    {clients.map((client, index) => (
                      <option key={index} value={client}>{client}</option>
                    ))}
                  </select>
                </div>
                
                {/* From Date - Entire Area Clickable */}
                <div className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <label 
                      htmlFor="start_date"
                      className="block text-sm font-medium text-gray-700 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      From Date {exportFilters.start_date && <span className="text-indigo-600">({exportFilters.start_date})</span>}
                    </label>
                    {exportFilters.start_date && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExportFilters(prev => ({...prev, start_date: ''}));
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Clear date"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div 
                    className="w-full p-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-600 focus-within:border-transparent cursor-pointer hover:border-gray-400 transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      const input = document.getElementById('start_date');
                      if (input) {
                        // Try showPicker first (modern browsers)
                        if (typeof input.showPicker === 'function') {
                          try {
                            input.showPicker();
                          } catch (error) {
                            // Fallback to focus and click
                            input.focus();
                            input.click();
                          }
                        } else {
                          // Fallback for older browsers
                          input.focus();
                          input.click();
                        }
                      }
                    }}
                  >
                    <input
                      id="start_date"
                      type="date"
                      value={exportFilters.start_date}
                      onChange={(e) => setExportFilters(prev => ({...prev, start_date: e.target.value}))}
                      className="w-full bg-transparent cursor-pointer focus:outline-none"
                    />
                  </div>
                </div>
                
                {/* To Date - Entire Area Clickable */}
                <div className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <label 
                      htmlFor="end_date"
                      className="block text-sm font-medium text-gray-700 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      To Date {exportFilters.end_date && <span className="text-indigo-600">({exportFilters.end_date})</span>}
                    </label>
                    {exportFilters.end_date && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExportFilters(prev => ({...prev, end_date: ''}));
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Clear date"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div 
                    className="w-full p-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-600 focus-within:border-transparent cursor-pointer hover:border-gray-400 transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      const input = document.getElementById('end_date');
                      if (input) {
                        // Try showPicker first (modern browsers)
                        if (typeof input.showPicker === 'function') {
                          try {
                            input.showPicker();
                          } catch (error) {
                            // Fallback to focus and click
                            input.focus();
                            input.click();
                          }
                        } else {
                          // Fallback for older browsers
                          input.focus();
                          input.click();
                        }
                      }
                    }}
                  >
                    <input
                      id="end_date"
                      type="date"
                      value={exportFilters.end_date}
                      onChange={(e) => setExportFilters(prev => ({...prev, end_date: e.target.value}))}
                      className="w-full bg-transparent cursor-pointer focus:outline-none"
                    />
                  </div>
                </div>
                {/* State Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    State
                  </label>
                  <select
                    value={exportFilters.state}
                    onChange={(e) => setExportFilters(prev => ({...prev, state: e.target.value}))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600"
                  >
                    <option value="">All States</option>
                    {states.map((state, index) => (
                      <option key={index} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Status
                  </label>
                  <select
                    value={exportFilters.status}
                    onChange={(e) => setExportFilters(prev => ({...prev, status: e.target.value}))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600"
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="assigned">Assigned</option>
                    <option value="sample_uploaded">Sample Uploaded</option>
                    <option value="sample_approved">Sample Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Column Selection */}
            <div className="bg-white border rounded-lg p-4 space-y-4">
              <h3 className="text-lg font-semibold">Column Selection</h3>
              <div className="space-y-4">
                {Object.entries(columnGroups).map(([groupName, columns]) => (
                  <div key={groupName} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">{groupName}</h4>
                      <button
                        onClick={() => toggleColumnGroup(groupName)}
                        className="text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        {Object.keys(columns).every(col => selectedColumns.includes(col)) ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(columns).map(([key, label]) => (
                        <label key={key} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={selectedColumns.includes(key)}
                            onChange={() => toggleColumn(key)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {selectedColumns.length} columns selected
              </div>
              <button
                onClick={executeExport}
                disabled={selectedColumns.length === 0 || isExporting}
                className="bg-indigo-600 text-white py-3 px-6 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors flex items-center"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Orders
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkImportExport;